import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [purpose, setPurpose] = useState("booking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("failed");
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.purpose) setPurpose(data.purpose);
        if (data.payment_status === "paid") { refresh(); return setState("paid"); }
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 8) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5">
      <div className="silk-card rounded-lg p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={38} className="animate-spin text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground">One moment while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && purpose === "featured" && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">You're featured ✦</h1>
            <p className="text-sm text-muted-foreground mb-8">Your listing now carries the verified badge and priority placement for the next 30 days.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-dashboard-link">
              <Link to="/dashboard">Back to My Studio</Link>
            </Button>
          </>
        )}
        {state === "paid" && purpose === "listing" && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">You're live ✦</h1>
            <p className="text-sm text-muted-foreground mb-8">Your Leader listing is active for the next 30 days — seekers can now find and book you.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-dashboard-link">
              <Link to="/dashboard">Back to My Studio</Link>
            </Button>
          </>
        )}
        {state === "paid" && purpose === "community" && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">Welcome to the Community ✦</h1>
            <p className="text-sm text-muted-foreground mb-8">Your Community Membership is active — the full Learn Library, Bridgett's Corner, and the Community Chat are yours.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-community-link">
              <Link to="/community">Enter the Community Chat</Link>
            </Button>
          </>
        )}
        {state === "paid" && purpose === "tip" && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">Thank you ♥</h1>
            <p className="text-sm text-muted-foreground mb-8">Your tip means the world to Bridgett — it keeps this community growing, one Ripple at a time.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-corner-link">
              <Link to="/corner">Back to Bridgett's Corner</Link>
            </Button>
          </>
        )}
        {state === "paid" && purpose === "gift" && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">Your gift is on its way ✦</h1>
            <p className="text-sm text-muted-foreground mb-8">We've emailed them a beautiful redemption link — they'll pick whatever day and time feels right. Thank you for sharing the ripple.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-home-link">
              <Link to="/">Back to O.R.E.C</Link>
            </Button>
          </>
        )}
        {state === "paid" && !["featured", "tip", "listing", "community", "gift"].includes(purpose) && (
          <>
            <CheckCircle2 size={42} className="text-primary mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2" data-testid="payment-confirmed-title">Session booked ♥</h1>
            <p className="text-sm text-muted-foreground mb-8">Your booking is confirmed — the ripple begins. You'll find all the details in My Bookings.</p>
            <Button asChild className="rounded-full px-8" data-testid="payment-success-bookings-link">
              <Link to="/bookings">View my bookings</Link>
            </Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={42} className="text-muted-foreground mx-auto mb-5" />
            <h1 className="font-display text-3xl text-foreground mb-2">
              {state === "failed" ? "Payment not completed" : "Still processing"}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              {state === "failed" ? "The payment didn't go through. No charge was made — you can try again anytime."
                : "The payment is taking longer than expected. Check My Bookings in a minute — if it confirmed, it will appear there."}
            </p>
            <Button asChild variant="outline" className="rounded-full px-8">
              <Link to="/bookings">Go to my bookings</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
